import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../api/client.js';
import { useDate, useMoney, useT } from '../i18n/index.js';
import { Badge, Banner, Button, Card, Spinner } from '../components/ui.js';
import { statusTone } from './OrdersPage.js';

export function OrderPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const t = useT();
  const money = useMoney();
  const formatDate = useDate();
  const [order, setOrder] = useState<Awaited<ReturnType<typeof api.getOrder>> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .getOrder(id)
      .then(setOrder)
      .catch((err) => setError((err as Error).message));
  }, [id]);

  if (error) return <div className="p-4"><Banner tone="error" title={t('app.error')}>{error}</Banner></div>;
  if (!order) return <Spinner label={t('app.loading')} />;

  const cancellable = ['AWAITING_PAYMENT', 'QUOTE_REQUESTED', 'PAID'].includes(order.status);

  return (
    <div className="mx-auto max-w-3xl space-y-4 px-4 py-6">
      <header className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-semibold">{order.reference}</h1>
        <Badge tone={statusTone(order.status)}>{t(`orders.statuses.${order.status}`)}</Badge>
        <span className="text-sm text-tinta-suave">{formatDate(order.createdAt)}</span>
      </header>

      <Card>
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase text-tinta-suave">
            <tr>
              <th className="py-1">SKU</th>
              <th>Talla</th>
              <th className="text-right">Uds.</th>
              <th className="text-right">Importe</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-borde">
            {order.lines.map((line) => (
              <tr key={line.variantId}>
                <td className="py-2">{line.sku}</td>
                <td>{line.size}</td>
                <td className="text-right tabular-nums">{line.qty}</td>
                <td className="text-right tabular-nums">{money(line.amount.cents)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <dl className="mt-3 space-y-1 border-t border-borde pt-3 text-sm">
          <div className="flex justify-between">
            <dt>{t('pricing.subtotal')}</dt>
            <dd className="tabular-nums">{money(order.subtotal.cents)}</dd>
          </div>
          <div className="flex justify-between text-tinta-suave">
            <dt>{t('pricing.vat')}</dt>
            <dd className="tabular-nums">{money(order.vat.cents)}</dd>
          </div>
          {order.walletDebit.cents > 0 && (
            <div className="flex justify-between text-ok">
              <dt>{t('checkout.wallet')}</dt>
              <dd className="tabular-nums">−{money(order.walletDebit.cents)}</dd>
            </div>
          )}
          <div className="flex justify-between text-base font-semibold">
            <dt>{t('pricing.total')}</dt>
            <dd className="tabular-nums">{money(order.amountDue.cents)}</dd>
          </div>
        </dl>
      </Card>

      {/* La firma del snapshot se ENSENA: el cliente puede comprobar que su
          pedido no se ha tocado desde que lo confirmo. */}
      <Card>
        <h2 className="text-sm font-semibold">Constancia del pedido</h2>
        <p className="mt-1 break-all font-mono text-xs text-tinta-suave">
          {order.snapshotSignature.algo} · {order.snapshotSignature.keyId} · {order.snapshotSignature.value.slice(0, 32)}…
        </p>
        <p className="mt-2 text-xs text-tinta-suave">
          El contenido del pedido quedó congelado y firmado al confirmarlo. No se recalcula aunque cambien tarifas o
          catálogo.
        </p>
      </Card>

      {/*
        PEDIR OTRA VEZ.

        El snapshot congelado guarda el diseño exacto de este pedido: repetirlo
        es leerlo, no reconstruirlo. Se abre un diseño NUEVO para que las
        cantidades y los detalles se puedan cambiar sin tocar el original.
      */}
      <Button
        variant="secondary"
        data-test="repetir-pedido"
        loading={busy}
        onClick={async () => {
          setBusy(true);
          try {
            const r = await api.repetirPedido(id);
            navigate(`/editor/${r.designId}`);
          } catch (err) {
            setError((err as Error).message);
          } finally {
            setBusy(false);
          }
        }}
      >
        {t('orders.repeat')}
      </Button>

      {cancellable && (
        <Button
          variant="danger"
          loading={busy}
          onClick={async () => {
            setBusy(true);
            try {
              setOrder(await api.cancelOrder(id));
            } catch (err) {
              setError((err as Error).message);
            } finally {
              setBusy(false);
            }
          }}
        >
          {t('orders.cancel')}
        </Button>
      )}
    </div>
  );
}
